import { useQuery } from '@tanstack/react-query';

import { SupplierSubscriptionPlan } from '../../../../../types/SupplierGroupSubscriptionMessage';
import { Link } from '../../../../cdl/Link/Link';
import { Tag } from '../../../../cdl/Tag/Tag';
import { Text } from '../../../../cdl/Text/Text';
import { queries } from '../../../../common/api/queryKeys/queries';
import { DataSheet } from '../../../../common/DataSheet/DataSheet';
import { AdvancedError } from '../../../../common/Error/AdvancedError';
import { formatDate } from '../../../../common/helpers/formatDate.helper';
import { formatNumber } from '../../../../common/helpers/formatNumber.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { Characters } from '../../../../common/helpers/unicode.helper';
import { useEnums } from '../../../../common/hooks/useEnums';
import { useRole } from '../../../../common/hooks/useRole';
import { InfoPopover } from '../../../../common/InfoPopover/InfoPopover';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../../common/ui/Box';
import { useSupplierSubscriptionReleaseFlags } from '../../../../supplier-subscription-plans/useSupplierSubscriptionReleaseFlags';

interface SupplierGroupPlanSectionProps {
    supplierGroupId: string;
}

export const SupplierGroupPlanSection = ({ supplierGroupId }: SupplierGroupPlanSectionProps) => {
    const role = useRole();
    const { getHumanReadableValue } = useEnums();
    const isSuperAdmin = role.isSuperAdmin();
    const isGroupAdmin = !isSuperAdmin && !!role.hasAnyAdminRights();
    const { earlySubscriptionSignup } = useSupplierSubscriptionReleaseFlags(isGroupAdmin);
    const enabled = isSuperAdmin || (isGroupAdmin && earlySubscriptionSignup);
    const subscriptionQuery = useQuery({
        ...queries.supplierGroups.detail(supplierGroupId)._ctx.subscription,
        enabled,
    });
    if (!enabled) {
        return null;
    }

    if (subscriptionQuery.isPending) {
        return <LoadingIndicator />;
    }
    if (subscriptionQuery.isError) {
        return <AdvancedError />;
    }

    const subscription = subscriptionQuery.data;
    const isBasic = subscription.plan === SupplierSubscriptionPlan.BASIC;
    const renewalAt = subscription.dateSubscriptionRenewal;
    const fee = subscription.transactionFeePercentage;

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Text variant="subtitle1" as="h2">
                {translate('supplierPlan.plansAndSubscription')}
            </Text>
            {!isSuperAdmin ? (
                <Box display="flex" flexDirection="column">
                    <Box display="flex" alignItems="center" gap={4} flexWrap="wrap" marginBottom={4}>
                        <Text variant="small" as="h3" fontWeight={500}>
                            {translate(
                                isBasic
                                    ? 'supplierPlan.unlockMoreFlexibilityVisibilityAndLowerCosts'
                                    : 'supplierPlan.thanksForSubscribing'
                            )}
                        </Text>
                        <Tag
                            variant={subscription.plan === SupplierSubscriptionPlan.ENTERPRISE ? 'default' : 'accent'}
                            emphasis={subscription.plan === SupplierSubscriptionPlan.ENTERPRISE}
                        >
                            {getHumanReadableValue(
                                'supplierSubscriptionPlan',
                                isBasic ? SupplierSubscriptionPlan.PROFESSIONAL : subscription.plan
                            )}
                        </Tag>
                    </Box>
                    {!isBasic ? (
                        <Text variant="small" color="foreground.muted">
                            {translate('supplierPlan.billedAnnuallyAndRenewsAutomatically')}
                        </Text>
                    ) : null}
                    <Text variant="small" color="foreground.muted">
                        {translate(
                            isBasic
                                ? 'supplierPlan.multipleOffersSchedulesAndPrioritySupport'
                                : 'supplierPlan.exploreIncludedFeatures'
                        )}
                        {Characters.NO_BREAK_SPACE}
                        <Link href="https://website.closelink.com/supplier" size="small">
                            {translate('sidebar.proFeaturesBanner.cta')}
                        </Link>
                    </Text>
                </Box>
            ) : null}
            <DataSheet>
                <DataSheet.Label>{translate('supplierPlan.currentPlan')}</DataSheet.Label>
                <Text variant="small">{getHumanReadableValue('supplierSubscriptionPlan', subscription.plan)}</Text>
                {!isBasic ? (
                    <>
                        <DataSheet.Label>{translate('supplierPlan.renewalDate')}</DataSheet.Label>
                        <Text variant="small">
                            {renewalAt !== undefined
                                ? formatDate({ date: renewalAt, timeZone: 'UTC' })
                                : Characters.EN_DASH}
                        </Text>
                    </>
                ) : null}
                <DataSheet.Label>{translate('supplierPlan.transactionFee')}</DataSheet.Label>
                <Box display="flex" alignItems="center" gap={4}>
                    <Text variant="small">
                        {fee !== undefined
                            ? translate('supplierPlan.feePercentage', { percentage: formatNumber({ number: fee }) })
                            : Characters.EN_DASH}
                    </Text>
                    <InfoPopover>{translate('supplierPlan.thisFeeAppliesOnlyToSpotMarketTransactions')}</InfoPopover>
                </Box>
            </DataSheet>
        </Box>
    );
};
